import apiClient from '../utils/axios-config';
import { logger } from '../utils/logger';
import enhancedAuthService from './enhanced-auth-service';

// Use the configured axios client which already has the base URL

export interface UserProfile {
  id: string;
  email: string;
  username?: string; // User's unique username (backend should provide this)
  displayName: string;
  profilePicture: string;
  isPrivate?: boolean;
  isVerified?: boolean; // User verification status
  followerCount?: number; // Number of followers
  followingCount?: number; // Number of following
  createdAt: Date;
  lastLogin: Date;
  likedOutfits: string[];
  uploadedOutfits: string[];
  tryOnProfilePictureUrl?: string; // Private profile picture for virtual try-on
  tryOnPictureUploadedAt?: Date; // Timestamp when try-on picture was uploaded
}

/**
 * User information cache to avoid repeated API calls
 */
class UserInfoCache {
  private cache = new Map<string, { user: UserProfile; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  get(userId: string): UserProfile | null {
    const cached = this.cache.get(userId);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(userId);
      return null;
    }

    return cached.user;
  }

  set(userId: string, user: UserProfile): void {
    this.cache.set(userId, {
      user,
      timestamp: Date.now(),
    });
  }

  clear(): void {
    this.cache.clear();
  }

  delete(userId: string): void {
    this.cache.delete(userId);
  }
}

const userCache = new UserInfoCache();

/**
 * Service for user-related API calls
 */
export class UserService {
  /**
   * Get user profile by ID with caching
   * @param userId User ID
   * @returns User profile or null if not found
   */
  async getUserById(userId: string): Promise<UserProfile | null> {
    try {
      // Check cache first
      const cachedUser = userCache.get(userId);
      if (cachedUser) {
        logger.info(`Retrieved user from cache: ${userId}`);
        return cachedUser;
      }

      // Fetch from API
      const response = await apiClient.get(`/users/${userId}`);

      // Handle the API response format: { success: true, user: {...} }
      const userData = response.data.success ? response.data.user : response.data;
      
      const user: UserProfile = {
        ...userData,
        createdAt: new Date(userData.createdAt),
        lastLogin: new Date(userData.lastLogin),
        tryOnPictureUploadedAt: userData.tryOnPictureUploadedAt 
          ? new Date(userData.tryOnPictureUploadedAt) 
          : undefined,
      };

      // Cache the result
      userCache.set(userId, user);
      logger.info(`Retrieved and cached user: ${userId}`);

      return user;
    } catch (error: any) {
      if (error.response?.status === 404) {
        logger.warn(`User not found: ${userId}`);
        return null;
      }
      logger.error(`Error fetching user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get multiple users by IDs with caching
   * @param userIds Array of user IDs
   * @returns Map of user ID to user profile
   */
  async getUsersByIds(userIds: string[]): Promise<Map<string, UserProfile>> {
    const result = new Map<string, UserProfile>();
    const uncachedIds: string[] = [];

    // Check cache for each user
    for (const userId of userIds) {
      const cachedUser = userCache.get(userId);
      if (cachedUser) {
        result.set(userId, cachedUser);
      } else {
        uncachedIds.push(userId);
      }
    }

    // Fetch uncached users
    if (uncachedIds.length > 0) {
      const fetchPromises = uncachedIds.map(async (userId) => {
        try {
          const user = await this.getUserById(userId);
          if (user) {
            result.set(userId, user);
          }
        } catch (error) {
          logger.warn(`Failed to fetch user ${userId}:`, error);
        }
      });

      await Promise.all(fetchPromises);
    }

    return result;
  }

  /**
   * Get current user profile (authenticated)
   * @returns Current user profile
   */
  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      const token = await enhancedAuthService.getAccessToken();
      if (!token) {
        return null;
      }

      const response = await apiClient.get(`/auth/me`);

      // Handle the API response format: { success: true, user: {...} }
      const userData = response.data.success ? response.data.user : response.data;
      
      const user: UserProfile = {
        ...userData,
        createdAt: new Date(userData.createdAt),
        lastLogin: new Date(userData.lastLogin),
        tryOnPictureUploadedAt: userData.tryOnPictureUploadedAt 
          ? new Date(userData.tryOnPictureUploadedAt) 
          : undefined,
      };

      // Cache the current user
      userCache.set(user.id, user);

      return user;
    } catch (error: any) {
      logger.error('Error fetching current user:', error);
      throw error;
    }
  }

  /**
   * Clear user cache
   */
  clearCache(): void {
    userCache.clear();
  }

  /**
   * Remove specific user from cache
   * @param userId User ID to remove from cache
   */
  removeFromCache(userId: string): void {
    userCache.delete(userId);
  }

  /**
   * Search users by username or display name
   * @param query Search query
   * @param options Search options
   * @returns Search results
   */
  async searchUsers(
    query: string,
    options: {
      limit?: number;
      offset?: number;
      sortBy?: 'relevance' | 'followers' | 'recent';
      includePrivate?: boolean;
    } = {}
  ): Promise<{
    users: UserProfile[];
    pagination: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
      nextOffset: number | null;
    };
    query: string;
    sortBy: string;
  }> {
    try {
      const {
        limit = 20,
        offset = 0,
        sortBy = 'relevance',
        includePrivate = false,
      } = options;

      const params = new URLSearchParams({
        q: query,
        limit: limit.toString(),
        offset: offset.toString(),
        sortBy,
        includePrivate: includePrivate.toString(),
      });

      const response = await apiClient.get(`/users/search?${params}`);

      const users: UserProfile[] = response.data.users.map((user: any) => ({
        ...user,
        createdAt: new Date(user.createdAt || Date.now()),
        lastLogin: new Date(user.lastLogin || Date.now()),
        likedOutfits: user.likedOutfits || [],
        uploadedOutfits: user.uploadedOutfits || [],
        tryOnPictureUploadedAt: user.tryOnPictureUploadedAt 
          ? new Date(user.tryOnPictureUploadedAt) 
          : undefined,
      }));

      // Cache the users
      users.forEach(user => {
        userCache.set(user.id, user);
      });

      return {
        users,
        pagination: response.data.pagination,
        query: response.data.query,
        sortBy: response.data.sortBy,
      };
    } catch (error: any) {
      logger.error('Error searching users:', error);
      throw error;
    }
  }
}

export default new UserService();